/* eslint-disable max-lines, max-depth, max-depth, max-statements, complexity */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import { apiErrorDefaultAction } from "../../../../../share/util/api/errorAction";
import { executePolling } from "../../../../../share/util/api/polling";
import apiService from "../../../../../share/util/api/service";
import alert from "../../../../../share/alert";
import cstmTable from "../../../../../share/components/vue/PaginationTable";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import config from "../../../../../share/config";
import util from "../../../../../share/util/utils";
import diagnose from "../../../../../share/util/diagnose";
import logWrapper from "../../../../../share/logWrapper";
import obdDataMonitorExecute from "../../../../vue/obd/individual/dataMonitor/ObdDataMonitorMeasure";
import dmUtil from "../../../dataMonitor/dataMonitorUtil";
import { convertKeyForPidToDid } from '../../../../../util/obd/pid';
import individualDiagnose from "../../../../../share/util/individualDiagnose";

const SIGNAL_GROUP_I18N_PREFIX = 'individual.select_signal_group_';
const reader = new FileReader();
const moment = require('moment');

/** モーダル表示用フェーズ定義 */
const PHASE = {
  CONFIRM_IMPORT: 'confirmImport',
  CONFIRM_EXPORT: 'confirmExport',
  IMPORT_SUCCESS: 'success',
  ERROR_FORMAT: 'errorFormat',
  ERROR_SYSTEM_ID: 'errorSystemId'
};

/**エラーメッセージ定義 */
const MESSAGE = {
  ERROR_SYSTEM_ID: 'invalid_system_id'
};

export default {
  name: "ObdDataMonitorSelect",
  data() {
    return {
      processing: false,
      cstmTableColumns: [],
      systemTableColumns: [],
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      filterRemoveFunctionList: [],
      // 選択中の機能オブジェクト
      selectedFunction: {},
      selectedFunctions: [],
      selectedRemoveFunction: {},
      selectedRemoveFunctions: [],
      selectedFunctionList: [],
      selectedFunctionListPre: [],
      showDataMonitorExecute: false,
      dataMonitorList: [],
      // フィルター用テキスト
      filterText: "",
      filterTextRemove: "",
      // データモニタ用のi18nKey
      i18nDmKey: "",
      menuResponse: null,
      params: null,
      systemId: "",
      previousName: "",
      queryName: "",
      sortAsc: true, // 項目並び順
      //リクエストID
      response: "",
      //モニタ項目
      monitorResponse: null,
      //項目ソート
      currentSortkey: "",
      // デバッグ環境か否か
      isDebug: process.env.VUE_APP_DEBUG_LOG && process.env.VUE_APP_DEBUG_LOG === "true",
      // デバッグ用のコンポーネントを表示するか否か
      showDebug: false,
      selectedSignalGroup: null,
      signalGroupList: [],
      // 選択中のシステムでフィルタした信号線グループ。定義が存在しないコンソールエラーが発生するため、使用しないが定義。
      filteredSignalGroupList: [],
      signalGroupItems: new Map(),
      // HTMLにてログ出力するため定義
      logWrapper: logWrapper,
      // 項目リスト対応
      importFile: null,
      fileName: "",
      showCstmListModal: false,
      phase: null
    };
  },

  props: {
    // システム間移動判定
    isUseCache: undefined,
    // DM/AT判定
    isDataMonitor: { type: Boolean, default: false, required: true },
    // 特殊機能判定
    isSpecial: { type: Boolean, default: false, required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // AT一覧情報（OBDでは不使用）
    selectedAtFunc: { type: Object, default: {}, required: false },
  },

  components: {
    "processing-view": loading,
    "cstm-table": cstmTable,
    "obd-data-monitor-execute": obdDataMonitorExecute,
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.monitorResponse) {
          return;
        }
        const menuList = this.getDmi18nMonitorItemList(
          this.monitorResponse,
        );
        this.filterFunctionList = menuList;
        this.originalFunctionList = menuList;
        this.selectedFunctionList = this.getDmi18nMenuSelectedList(
          this.selectedFunctionList,
        );
        this.selectedFunctionListPre = this.selectedFunctionList;
        this.updateFilter();
        this.filterRemoveFunctionList = this.selectedFunctionList;
        this.updateFilterRemove();
        this.setColumns();
        this.signalGroupList = this.signalGroupList.map((signal) => {
          return { value: signal.value, text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}${signal.value}`) };
        });
      },
    },

    isUseCache: {
      immediate: true,
      async handler(val) {
        if ((val === null) || (val === undefined)) {
          return;
        }
        this.setColumns();
        if (this.isDataMonitor) {
          await this.updateTable();
        } else {
          await this.updateTableForAT();
        }
        // 全項目追加
        this.appendFunction(this.originalFunctionList);
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      userProperty: (state) => state.userProperty,
      individualMenus: state => state.vehicle.individualMenus,
      obdDataId: state => state.obd.obdDataId,
      test: (state) => state.obd,
      systemName(state) {
        const systems = state.obd.systems;
        return systems.find(
          (func) => func.systemNo === this?.$route?.params?.systemNo
        ).systemName;
      },
    }),

    itemComparator() {
      if (this.sortAsc) {
        return (a, b) => a.sort_id - b.sort_id;
      } else {
        return (a, b) => b.sort_id - a.sort_id;
      }
    },

    itemPidComparator() {
      if (this.sortAsc) {
        return (a, b) => this.sortPidAscFunc(a, b);
      } else {
        return (a, b) => this.sortPidDescFunc(a, b);
      }
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnose.operationFrom(this.$router.currentRoute.value.name);
    },

    // 項目リスト関連のモーダル設定
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR_FORMAT:
        case PHASE.ERROR_SYSTEM_ID:
          return 'danger';
        case PHASE.IMPORT_SUCCESS:
          return 'success';
        case PHASE.CONFIRM_IMPORT:
        case PHASE.CONFIRM_EXPORT:
        default:
          return 'secondary';
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      const ConfirmTitle = 'message.common_title_confirmation';
      const SuccessTitle = 'message.common_title_success';
      const ErrorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.CONFIRM_IMPORT]: ConfirmTitle,
        [PHASE.CONFIRM_EXPORT]: ConfirmTitle,
        [PHASE.IMPORT_SUCCESS]: SuccessTitle,
        [PHASE.ERROR_FORMAT]: ErrorTitle,
        [PHASE.ERROR_SYSTEM_ID]: ErrorTitle
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * モーダル表示メッセージ判定
     * @returns {string} メッセージ
     */
    modalMessage() {
      const messageMap = {
        [PHASE.CONFIRM_IMPORT]: `individual.label_data_monitor_item_list_import_confirm`,
        [PHASE.CONFIRM_EXPORT]: `individual.label_data_monitor_item_list_export_confirm`,
        [PHASE.IMPORT_SUCCESS]: `individual.label_data_monitor_item_list_import_success`,
        [PHASE.ERROR_FORMAT]: `individual.label_data_monitor_item_list_import_invalid_format`,
        [PHASE.ERROR_SYSTEM_ID]: `individual.label_data_monitor_item_list_import_invalid_system_id`
      };

      return this.$t(messageMap[this.phase]);
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.CONFIRM_IMPORT]: true,
        [PHASE.CONFIRM_EXPORT]: true,
        [PHASE.IMPORT_SUCCESS]: true,
        [PHASE.ERROR_FORMAT]: true,
        [PHASE.ERROR_SYSTEM_ID]: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * OKボタンの押下可否判定
     * @returns {boolean} trueなら押下可能
     */
    isAbleOkButton() {
      // PHASE.CONFIRM_IMPORT 以外は常時押下可能
      if (this.phase !== PHASE.CONFIRM_IMPORT) return true;
      // インポート対象のファイルが指定されていれば押下可能
      return this.importFile ? true : false;
    },

    /**
     * エクスポートボタンの押下可否判定
     * @returns {boolean} trueなら押下可能
     */
    isAbleExportButton() {
      // 項目未選択時は押下不可
      if (this.selectedFunctionList.length === 0) {
        return false;
      } else {
        return true;
      }
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.CONFIRM_IMPORT]: true,
        [PHASE.CONFIRM_EXPORT]: true,
        [PHASE.IMPORT_SUCCESS]: false,
        [PHASE.ERROR_FORMAT]: false,
        [PHASE.ERROR_SYSTEM_ID]: false
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * インポートファイル選択の表示判定
     * @returns {boolean} trueなら表示
     */
    showInputArea() {
      const showInputAreaMap = {
        [PHASE.CONFIRM_IMPORT]: true,
        [PHASE.CONFIRM_EXPORT]: false,
        [PHASE.IMPORT_SUCCESS]: false,
        [PHASE.ERROR_FORMAT]: false,
        [PHASE.ERROR_SYSTEM_ID]: false
      };
      let result = showInputAreaMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * PID表の定義順にソートする処理
     */
    sortPidAscFunc(a, b) {
      const aSplit = a.data_monitor_id.split('_');
      const bSplit = b.data_monitor_id.split('_');
      if (aSplit[0] === bSplit[0] && aSplit[1] === bSplit[1]) {
        return Number(bSplit[2]) - Number(aSplit[2]);
      } else if (aSplit[0] === bSplit[0]) {
        return Number(aSplit[1]) - Number(bSplit[1]);
      } else {
        return parseInt(`0x${aSplit[0]}`, 16) - parseInt(`0x${bSplit[0]}`, 16);
      }
    },

    /**
     * PID表の定義順と逆順にソートする処理
     */
    sortPidDescFunc(a, b) {
      const aSplit = a.data_monitor_id.split('_');
      const bSplit = b.data_monitor_id.split('_');
      if (aSplit[0] === bSplit[0] && aSplit[1] === bSplit[1]) {
        return Number(aSplit[2]) - Number(bSplit[2]);
      } else if (aSplit[0] === bSplit[0]) {
        return Number(bSplit[1]) - Number(aSplit[1]);
      } else {
        return parseInt(`0x${bSplit[0]}`, 16) - parseInt(`0x${aSplit[0]}`, 16);
      }
    },

    /**
     * データモニタ項目一覧取得
     * @returns GetのResponse | データモニタ項目
     */
    async getMonitorItemsResponse() {
      const systemNo = this.$route.params.systemNo;
      const response = await executePolling(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await apiService.postCall(
            apiService.OBD,
            `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${systemNo}/dataMonitor`,
            {
              type: "setMonitorItems",
            },
            this.operationScreen,
          );
        },
        // GET API 呼び出しのコールバック
        async (postApiResponse) => {
          return await apiService.getCall(
            apiService.OBD,
            `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
            this.operationScreen,
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API レスポンスチェックのコールバックはデフォルト処理を使用
        undefined,
        // GET API レスポンスチェックのコールバックはデフォルト処理を使用
        undefined,
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );
      // 処理失敗時は response が null になるので考慮
      if (response) {
        // 翻訳ファイルに記載のないPID/DIDは表示させない
        const filteredMonitorResponse = response.data.results.monitor_items.filter(e => this.$te(`pid.${convertKeyForPidToDid(e.id)}`));
        this.updateSignalGroupInfo({
          monitor_items: filteredMonitorResponse,
          custom_list: response.data.results.custom_list,
        });
        return filteredMonitorResponse;
      } else {
        return null;
      }
    },

    /**
     * 信号グループ情報の更新を行う
     * @param {*} response データモニタ項目
     */
    updateSignalGroupInfo(response) {
      // 信号グループ情報の初期化
      this.signalGroupList = [
        { value: "all_data", text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}all_data`) },
        { value: "custom_list", text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}custom_list`) }
      ];
      this.signalGroupItems = new Map([
        ['all_data', response.monitor_items.map((item) => item.id)],
        ['custom_list', []]
      ]);

      if (response.custom_list) {
        this.signalGroupItems.set('custom_list', response.custom_list);
      }
    },

    /**
     * テーブル情報の更新を行う
     */
    async updateTable() {
      try {
        this.processing = true;
        this.monitorResponse = await this.getMonitorItemsResponse();
        if (this.monitorResponse) {
          const monitorDataList = this.getDmi18nMonitorItemList(
            this.monitorResponse,
          );
          this.originalFunctionList = monitorDataList;
          this.filterFunctionList = monitorDataList;
          this.updateFilter();

          // 表示項目がない場合、非サポート画面を表示
          if (this.originalFunctionList.length < 1) {
            // OK押下時は何もしない
            alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
          }
        }
      } finally {
        this.processing = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * データモニタの多言語変換の一覧取得
     * @param {Array} monitorResponse メニュー一覧
     */
    getDmi18nMonitorItemList(monitorResponse) {
      // 多言語変換リストの取得
      const originalMonitorResponse = monitorResponse.map((item) => {
        const i18nDmItemKey = `pid.${convertKeyForPidToDid(item.id)}`;
        return {
          data_monitor_id: item.id,
          sort_id: item.monitor_display_order,
          menu_title: util.trimStr(this.$t(i18nDmItemKey)),
        };
      });
      // monitor_display_order が null の場合はPID表に基づいてソート
      const displayOrderNullItems = originalMonitorResponse
        .filter(item => item.sort_id === null)
        .sort(this.itemPidComparator);
      // monitor_display_order が数値の場合はその順にソート
      const displayOrderNumberItems = originalMonitorResponse
        .filter(item => item.sort_id !== null)
        .sort(this.itemComparator);
      return displayOrderNullItems.concat(displayOrderNumberItems);
    },

    /**
     * データモニタの多言語変換の一覧取得
     * @param {Array} selectedList メニュー一覧
     */
    getDmi18nMenuSelectedList(selectedList) {
      // 多言語変換リストの取得
      const originalSelectedList = selectedList.map((item) => {
        const i18nDmItemKey = `pid.${convertKeyForPidToDid(item.data_monitor_id)}`;
        return {
          data_monitor_id: item.data_monitor_id,
          menu_title: util.trimStr(this.$t(i18nDmItemKey)),
          sort_id: item.sort_id,
        };
      });
      // monitor_display_order が null の場合はPID表に基づいてソート
      const displayOrderNullItems = originalSelectedList
        .filter(item => item.sort_id === null)
        .sort(this.itemPidComparator);
      // monitor_display_order が数値の場合はその順にソート
      const displayOrderNumberItems = originalSelectedList
        .filter(item => item.sort_id !== null)
        .sort(this.itemComparator);
      return displayOrderNullItems.concat(displayOrderNumberItems);
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRow: function (selected) {
      // cstm-tableのソート順を取得
      const sortDir =
        this.$refs.filtered_tbl.currentSortDir === config.ASC
          ? config.DESC
          : config.ASC;
      const sort_now = this.$refs.filtered_tbl.currentSort;

      this.selectedFunction = this.originalFunctionList.find(
        (func) => func.data_monitor_id === selected.data_monitor_id
      );
      const idx = this.selectedFunctions.findIndex(
        (item) => item.data_monitor_id === this.selectedFunction.data_monitor_id
      );

      if (idx === -1) {
        this.selectedFunctions.push(this.selectedFunction);
      } else {
        this.selectedFunctions.splice(idx, 1);
      }

      this.setHighLight();
      this.selectedRemoveFunction = {};
      this.setHighLightRemove();

      // cstm-tableのソートを再実行
      this.currentSortkey = sort_now;
      this.$refs.filtered_tbl.currentSort = sort_now;
      this.$refs.filtered_tbl.currentSortDir = sortDir;
      this.$refs.filtered_tbl.sort({ name: sort_now });
      this.$refs.filtered_tbl.getSortIcon();
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRowRemove: function (selected) {
      this.selectedRemoveFunction = selected;
      const idx = this.selectedRemoveFunctions.findIndex(
        (item) =>
          item.data_monitor_id === this.selectedRemoveFunction.data_monitor_id
      );

      if (idx === -1) {
        this.selectedRemoveFunctions.push(selected);
      } else {
        this.selectedRemoveFunctions.splice(idx, 1);
      }

      this.setHighLight();
      this.selectedFunction = {};
      this.setHighLightRemove();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.filterFunctionList.forEach((item) => {
        let matchFlg = false;
        this.selectedFunctions.forEach((data) => {
          if (item.data_monitor_id === data.data_monitor_id) {
            item.fieldSpec = {
              menu_title: config.LIST_ITEM_HIGHLIGHT,
            };
            matchFlg = true;
          }
        });
        if (!matchFlg) {
          delete item.fieldSpec;
        }
      });
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLightRemove() {
      this.filterRemoveFunctionList.forEach((item) => {
        let matchFlg = false;
        this.selectedRemoveFunctions.forEach((data) => {
          if (item.data_monitor_id === data.data_monitor_id) {
            item.fieldSpec = {
              menu_title: config.LIST_ITEM_HIGHLIGHT,
            };
            matchFlg = true;
          }
        });
        if (!matchFlg) {
          delete item.fieldSpec;
        }
      });
    },

    /**
     * 選択状態を解除し、フィルターの文字列、選択済みの項目に応じてリストをフィルターする
     */
    updateFilter() {
      this.currentSortkey = "";
      this.selectedFunctions = [];
      this.selectedFunction = {};
      const groupFilteredList = this.filterBySignalGroup(this.selectedSignalGroup);
      if (groupFilteredList && groupFilteredList.length > 0) {
        this.filterFunctionList = groupFilteredList.filter(
          // 選択済みの項目をフィルターする
          (item) =>
            !this.selectedFunctionList.some(selectedItem => selectedItem.data_monitor_id === item.data_monitor_id)
        ).filter(
          // 翻訳後の名称を大文字・小文字区別なくフィルターする
          (item) =>
            item.menu_title
              .toLowerCase()
              .includes(this.filterText.toLowerCase())
        );
      } else {
        this.filterFunctionList = [];
      }
      this.setHighLightRemove();
      this.setHighLight();
    },

    /**
     * 信号グループに応じてリストを絞り込む関数
     * @param {string} signalGroup 
     * @returns filteredList 信号グループでフィルターされた項目
     */
    filterBySignalGroup(signalGroup) {
      let filteredList = [];
      if (this.signalGroupList.some((group) => group.value === signalGroup)) {
        const filteredId = this.signalGroupItems.get(signalGroup);
        filteredList = this.originalFunctionList.filter((list) => filteredId.includes(list.data_monitor_id));
      } else if (signalGroup === null) {
        //初期状態
        filteredList = this.originalFunctionList;
      }
      return filteredList;
    },

    /**
     * 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする
     */
    updateFilterRemove() {
      this.selectedRemoveFunctions = [];
      this.selectedRemoveFunction = {};
      if (this.selectedFunctionList && this.selectedFunctionList.length > 0) {
        this.filterRemoveFunctionList = this.selectedFunctionList.filter(
          // 翻訳後の名称を大文字・小文字区別なくフィルターする
          (item) =>
            item.menu_title
              .toLowerCase()
              .includes(this.filterTextRemove.toLowerCase())
        );
      } else {
        this.filterRemoveFunctionList = [];
      }
      this.setHighLight();
      this.setHighLightRemove();
    },

    /**
     * カラムを設定
     */
    setColumns() {
      this.systemTableColumns = [
        {
          id: "menu_title",
          name: "menu_title",
          label: this.$t("individual.header_data_monitor_table_item"),
          useHtmlBind: true,
        },
      ];
      this.cstmTableColumns = [
        {
          id: "menu_title",
          name: "menu_title",
          label: this.$t("individual.header_data_monitor_table_item"),
          useHtmlBind: true,
        },
      ];
    },

    /**
    *  項目追加
    */
    appendFunction(functionList) {

      // 項目リストの有無チェック(初期化通信時にエラーが発生した場合のフェールセーフ)
      if (!functionList) return;

      // 項目リスト-選択項目リスト差分
      const tmpListFull = functionList
        .filter((e) => {
          return !this.selectedFunctionList.some(
            (ele) => e.data_monitor_id === ele.data_monitor_id
          );
        })
        .sort((a, b) => a.sort_id - b.sort_id);

      const tmplist = tmpListFull.slice(0, functionList.length).map((e) => {
        return {
          data_monitor_id: e.data_monitor_id,
          sort_id: e.sort_id,
          menu_title: e.menu_title,
          pullDownVisible: true, // 設定値プルダウンの有無
          selectItem: "1", // プルダウンの設定値
        };
      });

      this.selectedFunctionList = [...new Set(this.selectedFunctionList.concat(tmplist))];
      this.selectedFunctionListPre = JSON.parse(
        JSON.stringify(this.selectedFunctionList)
      );
      this.selectedFunction = {};
      this.selectedFunctions = [];
      this.selectedRemoveFunction = {};
      this.selectedRemoveFunctions = [];
      this.setHighLight();
      this.filterRemoveFunctionList = this.selectedFunctionList;
      this.updateFilterRemove();
      this.updateFilter();
    },

    /**
     *  追加ボタン押下時
     */
    handleAppendButtonClick() {
      this.appendFunction(this.selectedFunctions);
    },

    /**
     * 削除ボタン押下時
     */
    handleRemoveButtonClick() {
      this.selectedRemoveFunctions.forEach((data) => {
        const idx = this.selectedFunctionList.findIndex(
          (item) => item.data_monitor_id === data.data_monitor_id
        );
        if (idx > -1) {
          this.selectedFunctionListPre.splice(idx, 1);
          this.selectedFunctionList.splice(idx, 1);
        }
      });

      this.selectedRemoveFunction = {};
      this.selectedRemoveFunctions = [];
      this.selectedFunction = {};
      this.selectedFunctions = [];
      this.setHighLight();
      this.filterRemoveFunctionList = this.selectedFunctionList;
      this.updateFilterRemove();
      this.updateFilter();
    },

    /**
     * 全追加ボタン押下時
     */
    handleAllAppendButtonClick() {
      this.appendFunction(this.filterFunctionList);
    },

    /**
     * 全削除ボタン押下時
     */
    handleAllRemoveButtonClick() {
      this.selectedRemoveFunction = {};
      this.selectedRemoveFunctions = [];
      this.selectedFunction = {};
      this.selectedFunctions = [];
      this.selectedFunctionList = [];
      this.selectedFunctionListPre = [];
      this.filterRemoveFunctionList = [];
      this.setHighLight();
      this.setHighLightRemove();
      this.updateFilter();
    },

    /** 計測開始 */
    startExecute() {
      if (this.selectedFunctionList && this.selectedFunctionList.length === 0 && this.isDataMonitor)
        return;
      // custom_listの更新
      this.signalGroupItems.set('custom_list', this.selectedFunctionList.map((list) => list.data_monitor_id));
      this.$emit("start-execute", this.selectedFunctionList);
    },

    /**
     * その他のエラー
     */
    showUnexpectedError() {
      alert.showError(
        this,
        null,
        this.$t("fixed_resource.obd_function_common_message_result_ng_2"),
        () => { }
      );
    },

    /**
     * 機能リストのエスケープ
     * @returns エスケープした機能リスト
     */
    escapeFunctionList(items) {
      return items.map(i => ({
        ...i,
        menu_title: util.escapeHTML(i.menu_title)
      })
      );
    },

    /**
     * TSVファイルダウンロード処理(DEBUG用)
     */
    downloadTSV() {
      const tsvItems = this.filterFunctionList.map((filterFunction, index) => ({
        data_monitor_id: filterFunction.data_monitor_id,
        name: filterFunction.menu_title,
        order: index
      }));
      dmUtil.tsvFileExport(tsvItems, 'data_monitor_select_list.tsv');
    },

    /**
    * デバッグ用のコンポーネントを表示する(DEBUG用)
    */
    showDebugButton() {
      if (this.isDebug) {
        this.showDebug = true;
      }
    },

    /**
     * 項目リストのインポート押下
     */
    showConfigImportModal() {
      // 初期化
      this.importFile = null;
      this.fileName = null;
      this.phase = PHASE.CONFIRM_IMPORT;
      this.showCstmListModal = true;
    },

    /**
     * 項目リストのエクスポート押下
     */
    showConfigExportModal() {
      this.phase = PHASE.CONFIRM_EXPORT;
      this.showCstmListModal = true;
    },

    /**
     * ファイル選択のダイアログを表示
     */
    openFolder() {
      this.$refs.input.click();
    },

    /**
     * 選択されたファイル情報を保持
     * @param {} file 
     */
    selectFile(file) {
      const fileData = file.target.files[0];
      if (fileData) {
        this.importFile = fileData;
        this.fileName = fileData.name;
      }
    },

    /**
     * インポート実行
     */
    importItemList() {
      reader.onload = (event) => {
        this.processing = true;
        individualDiagnose.getCurrentSystemId(this);
        let _importedItemList;
        try {
          _importedItemList = JSON.parse(event.target.result);
          // 指定ファイルのシステムIDが選択しているECUと一致しているか確認
          const tmpArray = [];
          _importedItemList.forEach((item) => {
            if (this.systemName === item.system) {
              tmpArray.push(item);
            }
          });
          // システムIDが一致しているデータがない場合は、システムID不一致エラーに遷移
          if (tmpArray.length === 0) throw new Error(MESSAGE.ERROR_SYSTEM_ID);
          // 選択済みの項目をリセット
          this.handleAllRemoveButtonClick();
          // 指定ファイルからselectedFunctionsを生成
          this.createItemListFromImport(tmpArray);
          this.appendFunction(this.selectedFunctions);
          this.phase = PHASE.IMPORT_SUCCESS;
        } catch (error) {
          if (error.message === MESSAGE.ERROR_SYSTEM_ID) {
            // システムID不一致エラー
            this.phase = PHASE.ERROR_SYSTEM_ID;
          } else {
            // フォーマットエラー
            this.phase = PHASE.ERROR_FORMAT;
          }
        } finally {
          this.processing = false;
        }
      };
      reader.readAsText(this.importFile);
    },

    /**
     * エクスポート実行
     */
    exportItemList() {
      // 選択済みリストの情報json化
      const tmpData = this.getExportData();
      // ファイル生成用のオブジェクト作成
      const link = window.document.createElement('a');
      const blob = new Blob([tmpData], { type: 'application/json;charset=utf-8' });
      try {
        // 日時情報の取得
        const date = moment().format("YYYYMMDDHHmmss");
        // 生成したファイルのダウンロード用処理
        link.download = 'itemList_' + date + '.json';
        link.href = window.URL.createObjectURL(blob);
        link.style.display = 'none';
        link.click();
      } catch (e) {
        // エラーログ出力
        this.logWrapper.log('item list export error.');
      } finally {
        // 生成したオブジェクトの解放
        if (link) link.remove();
        // モーダル非表示
        this.showCstmListModal = false;
      }
    },

    /**
     * エクスポート時のjson生成
     * @returns {JSON}
     */
    getExportData() {
      // システムIDとPIDの取得
      // 出力対象のリストから取得※data_monitor_idのタブ区切り1要素目がシステムID,2要素目がDID
      const tmpArray = [];
      this.selectedFunctionList.forEach((item) => {
        const obj = {
          "system": this.systemName,
          "id": item.data_monitor_id
        };
        tmpArray.push(obj);
      });

      return JSON.stringify(tmpArray);
    },

    /**
     * インポートファイルからthis.selectedFunctionsを生成
     * @param {Object} importedList 
     */
    createItemListFromImport(importedList) {
      // 初期化
      this.selectedFunctions = [];
      importedList.forEach((item) => {
        // data_monitor_idをキーにして他の情報を取得
        const obj = this.originalFunctionList.find(l => l.data_monitor_id === item.id);
        if (obj) this.selectedFunctions.push(obj);
      });
    },

    /**
     * 文言の設定
     */
    setPlaceHolderMessage() {
      return this.$t('individual.label_data_monitor_item_list_import_form_placeholder');
    },

    /**
     * 共通の[キャンセル]ボタンクリック処理
     */
    onCancelClicked() {
      // 確認画面でしか使用されないため分岐なし
      this.showCstmListModal = false;
    },

    /**
     * 共通の[OK]ボタンクリック処理
     */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM_IMPORT:
          // インポート実行
          this.importItemList();
          break;
        case PHASE.CONFIRM_EXPORT:
          // エクスポート実行
          this.exportItemList();
          break;
        case PHASE.IMPORT_SUCCESS:
        case PHASE.ERROR_FORMAT:
        case PHASE.ERROR_SYSTEM_ID:
          this.showCstmListModal = false;
          break;
        default:
          // noop
          break;
      }
    }
  },

  created() {
    // noop
  },

  mounted() {
    // noop
  },

  // Clear Timer
  clearWatcher: function () {
    if (this.watcher) {
      this.$stopInterval(this.watcher);
      this.watcher = null;
    }
    this.executeRspMsgCheckTimeout = Number(
      process.env.VUE_APP_MAC_CC_RSP_MAX_INTERVAL
    );
  },
};
